import React, { useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Descriptions, 
  Tag, 
  message, 
  Spin, 
  Empty, 
  Modal,
  Tabs,
  Space,
  Statistic,
  Alert,
  Progress,
  List,
  Table,
  Tooltip
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import WorkspaceModal from '../../components/modal/WorkspaceModal';
import { getWorkspaceById, deleteWorkspaceCompletely, calculateAndUpdateWorkspaceRecordsCount } from '../../api/workspaces';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const { Title } = Typography;

const WorkspaceDetailsPage: React.FC = () => {
  const { t } = useTranslation(['workspaces', 'common']);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stateWorkspace = location.state?.workspace as any | undefined;
  
  // State for modals and deletion process
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionProgress, setDeletionProgress] = useState(0);
  const [deletionStatus, setDeletionStatus] = useState<string>('');
  const [deletionResult, setDeletionResult] = useState<{
    success: boolean;
    deletedCounts: Record<string, number>;
    errors: string[];
  } | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const { data: workspace, isLoading, error } = useQuery<any | null>({
    queryKey: ['workspace', id],
    queryFn: () => getWorkspaceById(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes - shorter for records count updates
    refetchOnMount: true, // Allow refetch on mount
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Mutation to refresh records count
  const refreshRecordsCountMutation = useMutation({
    mutationFn: () => calculateAndUpdateWorkspaceRecordsCount(id!),
    onSuccess: () => {
      // Invalidate and refetch the workspace query
      queryClient.invalidateQueries({ queryKey: ['workspace', id] });
      queryClient.refetchQueries({ queryKey: ['workspace', id] });
      message.success('Records count updated successfully');
    },
    onError: (error: any) => {
      message.error('Failed to update records count: ' + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      setIsDeleting(true);
      setDeletionProgress(0);
      setDeletionStatus('Starting workspace deletion...');
      setDeletionResult(null);
      
      try {
        const result = await deleteWorkspaceCompletely(workspaceId, (progress: number, status: string) => {
          setDeletionProgress(progress);
          setDeletionStatus(status);
        });
        setDeletionResult(result);
        return result;
      } finally {
        setIsDeleting(false);
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        message.success(t('workspaces:success.delete', 'Workspace deleted successfully'));
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        navigate('/workspaces');
      } else {
        message.warning('Workspace deleted with some errors. Check console for details.');
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        navigate('/workspaces');
      }
    },
    onError: (error: any) => {
      message.error(error.message || t('workspaces:errors.deleteFailed', 'Failed to delete workspace'));
    },
  });

  // Handlers
  const handleEdit = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setIsModalVisible(false);
    queryClient.invalidateQueries({ queryKey: ['workspace', id] });
  }, [queryClient, id]);

  const handleEditCancel = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleDelete = useCallback(() => {
    const workspaceData = workspace || stateWorkspace;
    if (!workspaceData) return;
    
    Modal.confirm({
      title: 'Delete Workspace Completely',
      content: (
        <div>
          <Alert
            message="Warning: This action cannot be undone!"
            description={
              <div>
                <p>This will permanently delete the workspace <strong>"{workspaceData.name}"</strong> and ALL related data including:</p>
                <ul style={{ marginTop: 8, marginBottom: 8 }}>
                  <li>All apartment units, blocks, and property data</li>
                  <li>All residents, employees, and users</li>
                  <li>All maintenance requests, incidents, and tasks</li>
                  <li>All financial records (payables, receivables, payrolls)</li>
                  <li>All communications (chat threads, notifications, news)</li>
                  <li>All other workspace-related data across 30+ collections</li>
                </ul>
                <p><strong>This is a comprehensive deletion that will remove thousands of records.</strong></p>
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>Are you absolutely sure you want to proceed?</p>
        </div>
      ),
      okText: 'Delete Everything',
      okType: 'danger',
      cancelText: t('common:cancel', 'Cancel'),
      width: 600,
      onOk: () => deleteMutation.mutate(workspaceData.id),
    });
  }, [workspace, stateWorkspace, deleteMutation, t]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Use workspace from query or fallback to stateWorkspace
  const currentWorkspace = workspace || stateWorkspace;

  // Error state
  if (error) {
    return (
      <Card>
        <Empty 
          description={t('common:errors.loadingError', 'Error loading workspace data')} 
        />
      </Card>
    );
  }

  // No workspace found
  if (!currentWorkspace) {
    return (
      <Card>
        <Empty 
          description={t('common:errors.notFound', { entity: t('workspaces:title', 'Workspace') })} 
        />
      </Card>
    );
  }

  // Prepare collection data from workspace records_count
  const collectionData = currentWorkspace?.records_count ? Object.entries(currentWorkspace.records_count)
    .filter(([key]) => key !== 'lastUpdated')
    .map(([name, count]) => ({
      name,
      count: count as number,
      displayName: name.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')
    })) : [];

  const totalRecords = collectionData.reduce((sum, item) => sum + item.count, 0);

  // Control Panel table columns
  const controlPanelColumns = [
    {
      title: t('workspaces:controlPanel.collection', 'Collection'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text: string, record: any) => (
        <Space>
          <Typography.Text strong>{text}</Typography.Text>
          <Tag color="blue">{record.name}</Tag>
        </Space>
      ),
    },
    {
      title: t('workspaces:controlPanel.recordCount', 'Record Count'),
      dataIndex: 'count',
      key: 'count',
      render: (count: number) => (
        <Tag color={count > 0 ? 'green' : 'default'}>
          {count.toLocaleString()}
        </Tag>
      ),
    },
    {
      title: t('workspaces:controlPanel.actions', 'Actions'),
      key: 'actions',
      width: 200,
      render: (record: any) => (
        <Space wrap>
          <Tooltip title={t('workspaces:controlPanel.viewRecords', 'View Records')}>
            <Button 
              size="small" 
              disabled={record.count === 0}
              onClick={() => {
                // TODO: Implement view records functionality
                message.info(`Viewing ${record.count} records in ${record.displayName}`);
              }}
            >
              {t('common:view', 'View')}
            </Button>
          </Tooltip>
          <Tooltip title={t('workspaces:controlPanel.deleteAllRecords', 'Delete All Records')}>
            <Button 
              size="small" 
              danger 
              disabled={record.count === 0}
              onClick={() => {
                Modal.confirm({
                  title: t('workspaces:controlPanel.confirmDeleteCollection', 'Delete All Records'),
                  content: t('workspaces:controlPanel.confirmDeleteCollectionContent', 
                    `Are you sure you want to delete all ${record.count} records from ${record.displayName}? This action cannot be undone.`),
                  okText: t('common:delete', 'Delete'),
                  okType: 'danger',
                  cancelText: t('common:cancel', 'Cancel'),
                  onOk: () => {
                    // TODO: Implement delete collection records functionality
                    message.info(`Deleting all records from ${record.displayName}`);
                  },
                });
              }}
            >
              {t('common:delete', 'Delete')}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Tab items
  const items = [
    {
      key: 'overview',
      label: t('workspaces:tabs.overview', 'Overview'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('workspaces:form.status', 'Status')}>
              <Tag color={currentWorkspace.status === 'active' ? 'success' : 'default'}>
                {String(t(`workspaces:status.${currentWorkspace.status}`, currentWorkspace.status))}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:form.customer', 'Customer')}>
              {currentWorkspace.customer_name || currentWorkspace.customer_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.name', 'Name')}>
              {currentWorkspace.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.slug', 'Slug')}>
              {currentWorkspace.slug || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.language', 'Language')}>
              {currentWorkspace.language || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.currency', 'Currency')}>
              {currentWorkspace.currency || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'president',
      label: t('workspaces:tabs.president', 'President'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('workspaces:table.presidentName', 'President Name')}>
              {currentWorkspace.presidentName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.presidentEmail', 'President Email')}>
              {currentWorkspace.presidentEmail || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('workspaces:table.presidentPhone', 'President Phone')}>
              {currentWorkspace.presidentPhone || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'stats',
      label: t('workspaces:tabs.stats', 'Statistics'),
      children: currentWorkspace.stats ? (
        <Card>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title={t('workspaces:totalUnits', 'Total Units')}
                value={currentWorkspace.stats.total_units || 0}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={t('workspaces:totalUsers', 'Total Users')}
                value={currentWorkspace.stats.total_users || 0}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={t('workspaces:totalBlocks', 'Total Blocks')}
                value={currentWorkspace.stats.total_blocks || 0}
              />
            </Col>
          </Row>
        </Card>
      ) : <Empty description={t('common:messages.noData', 'No data available')} />,
    },
    {
      key: 'control-panel',
      label: t('workspaces:tabs.controlPanel', 'Control Panel'),
      children: (
        <Card>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic
                title={t('workspaces:controlPanel.totalCollections', 'Total Collections')}
                value={collectionData.length}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title={t('workspaces:controlPanel.totalRecords', 'Total Records')}
                value={totalRecords}
              />
            </Col>
            <Col span={6}>
              <Button 
                type="primary" 
                onClick={() => refreshRecordsCountMutation.mutate()}
                loading={refreshRecordsCountMutation.isPending}
                style={{ marginTop: 8 }}
              >
                {t('workspaces:controlPanel.refreshCounts', 'Refresh Counts')}
              </Button>
            </Col>
            <Col span={6}>
              <Button 
                type="primary" 
                danger
                onClick={handleDelete}
                loading={deleteMutation.isPending}
                style={{ marginTop: 8 }}
              >
                {t('workspaces:controlPanel.deleteAllData', 'Delete All Data')}
              </Button>
            </Col>
          </Row>

          {currentWorkspace?.records_count?.lastUpdated && (
            <Alert
              message={t('workspaces:controlPanel.lastUpdated', 'Last Updated')}
              description={new Date(currentWorkspace.records_count.lastUpdated).toLocaleString()}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Alert
            message={t('workspaces:controlPanel.warning', 'Warning')}
            description={t('workspaces:controlPanel.warningDescription', 
              'The actions in this control panel can permanently delete data. Please use with caution.')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {!currentWorkspace?.records_count ? (
            <Empty 
              description="No records count data available. Click 'Refresh Counts' to calculate collection statistics."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              columns={controlPanelColumns}
              dataSource={collectionData}
              loading={refreshRecordsCountMutation.isPending}
              rowKey="name"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} collections`,
              }}
              size="small"
              scroll={{ x: 600 }}
            />
          )}
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/workspaces')} />
          <Title level={3} style={{ margin: 0 }}>{currentWorkspace.name}</Title>
        </Col>
        <Col>
          <Space wrap>
            <Button 
              icon={<EditOutlined />} 
              className="ant-btn-primary"
              onClick={handleEdit}
            >
              {t('common:edit', 'Edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              {t('common:delete', 'Delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs 
        items={items} 
        activeKey={activeTab}
        onChange={setActiveTab}
      />

      {/* Edit Modal */}
      <WorkspaceModal
        open={isModalVisible}
        onSave={handleEditSuccess}
        onCancel={handleEditCancel}
        workspace={currentWorkspace}
      />

      {/* Deletion Progress Modal */}
      <Modal
        title="Deleting Workspace"
        open={isDeleting}
        footer={null}
        closable={false}
        width={600}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="large" style={{ marginBottom: 16 }} />
          <Typography.Title level={4} style={{ marginBottom: 16 }}>
            Deleting Workspace and All Related Data
          </Typography.Title>
          <Progress 
            percent={deletionProgress} 
            status="active"
            style={{ marginBottom: 16 }}
          />
          <Typography.Text type="secondary">
            {deletionStatus}
          </Typography.Text>
          <Alert
            message="Please do not close this window during deletion"
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      </Modal>

      {/* Deletion Result Modal */}
      <Modal
        title="Deletion Complete"
        open={!!deletionResult && !isDeleting}
        onOk={() => setDeletionResult(null)}
        onCancel={() => setDeletionResult(null)}
        width={700}
      >
        {deletionResult && (
          <div>
            <Alert
              message={deletionResult.success ? "Deletion Successful" : "Deletion Completed with Errors"}
              description={deletionResult.success 
                ? "All workspace data has been successfully deleted."
                : "Workspace was deleted but some errors occurred during the process."
              }
              type={deletionResult.success ? "success" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Typography.Title level={5}>Deletion Summary:</Typography.Title>
            <List
              size="small"
              dataSource={Object.entries(deletionResult.deletedCounts).filter(([_, count]) => count > 0)}
              renderItem={([collection, count]) => (
                <List.Item>
                  <Typography.Text strong>{collection}:</Typography.Text>
                  <Typography.Text>{count} documents deleted</Typography.Text>
                </List.Item>
              )}
              style={{ marginBottom: 16 }}
            />

            {deletionResult.errors.length > 0 && (
              <div>
                <Typography.Title level={5} type="danger">Errors:</Typography.Title>
                <List
                  size="small"
                  dataSource={deletionResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Typography.Text type="danger">{error}</Typography.Text>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WorkspaceDetailsPage; 